import type { Movimiento } from '@/types/database'

/**
 * ORR-224 — Estado de cuenta como resumen por período (opción A elegida por Lisandro):
 * una fila por mes con Debe / Pagado / Pendiente, en lugar de la lista cronológica de
 * movimientos.
 *
 * La decisión de diseño que importa: **las filas salen de las DEUDAS, nunca de los pagos**.
 * Un pago puede imputarse a deudas de varios meses, así que no tiene un período propio; su
 * aporte ya está registrado en el `monto_aplicado` de cada deuda que canceló. Armar el
 * resumen desde las deudas disuelve los tres defectos que se venían reportando:
 *
 *  - El imputado dejaba de verse (la fila de Deuda tiene `haber = 0` siempre, así que el
 *    PDF mostraba "Debe 47.200 | Pendiente 40.000 | Haber vacío" sin explicar la diferencia).
 *  - Un mes ya cancelado reaparecía en el haber, porque la fila del pago conserva para
 *    siempre el concepto autogenerado con el mes de las deudas que se tildaron al cobrar.
 *  - El pago se contaba dos veces cuando cancelaba una deuda anterior al período filtrado.
 *
 * El excedente sin imputar no se pierde: se acumula en `saldoAFavor` y se resta del saldo
 * final. Antes no aparecía en ninguna columna.
 */

export interface FilaResumenPeriodo {
  /** `YYYY-MM`, para ordenar y como key de React. */
  clave: string
  /** Etiqueta legible, p. ej. "Mayo 2026". */
  label: string
  debe: number
  pagado: number
  pendiente: number
}

export interface ResumenEstadoCuenta {
  filas: FilaResumenPeriodo[]
  totales: {
    debe: number
    pagado: number
    pendiente: number
    /** Excedente de pagos que todavía no se imputó a ninguna deuda. */
    saldoAFavor: number
    /** Lo que el cliente realmente adeuda: pendiente − saldo a favor. */
    saldoFinal: number
  }
}

type MovimientoResumible = Pick<Movimiento, 'tipo' | 'fecha' | 'debe' | 'mes_anio'> & {
  _aplicacionMontoAplicado?: number | null
  _aplicacionSaldoPendiente?: number | null
}

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]

/**
 * `movimientos.mes_anio` es texto libre en español ("Mayo 2026") generado por el MonthPicker,
 * y es nullable — el propio formulario lo rotula "Periodo (opcional)". Cuando no se puede
 * interpretar, se cae a `fecha`, que es NOT NULL y `YYYY-MM-DD`.
 */
const claveDesdeMesAnio = (mesAnio: string | null | undefined): string | null => {
  if (!mesAnio) return null
  const partes = mesAnio.trim().toLowerCase().split(/\s+/)
  if (partes.length < 2) return null
  const mes = MESES.indexOf(partes[0])
  const anio = Number(partes[1])
  if (mes < 0 || !Number.isInteger(anio) || anio < 1900 || anio > 2999) return null
  return `${anio}-${String(mes + 1).padStart(2, '0')}`
}

/** Recorta `YYYY-MM` sin construir un Date: evita el corrimiento de día por timezone. */
const claveDesdeFecha = (fecha: string): string => fecha.slice(0, 7)

export const labelDesdePeriodo = (clave: string): string => {
  const [anio, mes] = clave.split('-')
  const nombre = MESES[Number(mes) - 1]
  if (!nombre) return clave
  return `${nombre.charAt(0).toUpperCase()}${nombre.slice(1)} ${anio}`
}

export const resumirPorPeriodo = (
  movimientos: MovimientoResumible[],
  opciones: { soloPendientes?: boolean } = {},
): ResumenEstadoCuenta => {
  const { soloPendientes = false } = opciones
  const porPeriodo = new Map<string, FilaResumenPeriodo>()
  let saldoAFavor = 0

  for (const mov of movimientos) {
    // Para un Pago, `_aplicacionSaldoPendiente` es el saldo A FAVOR (excedente sin imputar).
    if (mov.tipo === 'Pago') {
      saldoAFavor += mov._aplicacionSaldoPendiente ?? 0
      continue
    }
    // Los Gastos no son del cliente (van con cliente_id NULL) y ya se excluyen aguas arriba.
    if (mov.tipo !== 'Deuda') continue

    const clave = claveDesdeMesAnio(mov.mes_anio) ?? claveDesdeFecha(mov.fecha)
    const debe = mov.debe ?? 0
    const pendiente = mov._aplicacionSaldoPendiente ?? debe
    const pagado = mov._aplicacionMontoAplicado ?? debe - pendiente

    const fila = porPeriodo.get(clave) ?? {
      clave,
      label: labelDesdePeriodo(clave),
      debe: 0,
      pagado: 0,
      pendiente: 0,
    }
    fila.debe += debe
    fila.pagado += pagado
    fila.pendiente += pendiente
    porPeriodo.set(clave, fila)
  }

  const filas = [...porPeriodo.values()]
    .filter((f) => !soloPendientes || f.pendiente > 0)
    .sort((a, b) => a.clave.localeCompare(b.clave))

  const totales = filas.reduce(
    (acc, f) => ({
      debe: acc.debe + f.debe,
      pagado: acc.pagado + f.pagado,
      pendiente: acc.pendiente + f.pendiente,
    }),
    { debe: 0, pagado: 0, pendiente: 0 },
  )

  return {
    filas,
    totales: {
      ...totales,
      saldoAFavor,
      saldoFinal: totales.pendiente - saldoAFavor,
    },
  }
}

export interface FilaMovimientoPDF {
  fecha: string
  concepto: string
  nroComprobante?: string
  debe: number
  /** Saldo pendiente de la deuda (descontados pagos parciales). 0 en los pagos. */
  pendiente: number
  haber: number
  /** Saldo acumulado hasta este movimiento inclusive. */
  saldo: number
}

type MovimientoPDF = Pick<Movimiento, 'tipo' | 'fecha' | 'concepto' | 'debe' | 'haber'> & {
  comprobante_numero?: string | null
  _aplicacionSaldoPendiente?: number | null
}

/**
 * ORR-272 — arma el detalle movimiento por movimiento del PDF.
 *
 * Los movimientos tienen que venir ya ordenados cronológicamente: la función acumula el
 * saldo en el orden en que los recibe y no reordena.
 *
 * Las dos reglas que no son obvias, y que ya causaron bugs (ORR-108, ORR-224):
 *
 *  - **El saldo acumula el NETO** (`pendiente − haber imputado`), pero las columnas Debe y
 *    Haber muestran el BRUTO. Una deuda de 40.000 con 7.200 ya pagados suma 32.800 al saldo
 *    y sigue mostrando 40.000 en Debe.
 *  - **De un pago solo cuenta lo que imputó.** Su `_aplicacionSaldoPendiente` es el excedente
 *    sin imputar; el excedente no baja el saldo porque todavía no canceló ninguna deuda.
 */
export const armarFilasMovimientos = (
  movimientos: MovimientoPDF[],
  saldoInicial = 0,
): { filas: FilaMovimientoPDF[]; totales: { totalDebe: number; totalPendiente: number; totalHaber: number; saldo: number } } => {
  let totalDebe = 0
  let totalHaber = 0
  let totalPendiente = 0
  let saldo = saldoInicial

  const filas = movimientos.map((mov) => {
    const debe = mov.debe ?? 0
    const haber = mov.haber ?? 0
    const pendiente = mov.tipo === 'Deuda' ? (mov._aplicacionSaldoPendiente ?? debe) : 0
    const haberImputado = mov.tipo === 'Pago' ? (mov._aplicacionSaldoPendiente ?? haber) : 0

    saldo += pendiente - haberImputado
    totalDebe += debe
    totalHaber += haber
    totalPendiente += pendiente

    return {
      fecha: mov.fecha,
      concepto: mov.concepto,
      nroComprobante: mov.comprobante_numero ?? undefined,
      debe,
      pendiente,
      haber,
      saldo,
    }
  })

  return { filas, totales: { totalDebe, totalPendiente, totalHaber, saldo } }
}
